import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (matched to legacy admin's POSTs):
//
//   POST /admin/list_user_contact   { page, limit, search?, sortyBy?, sortDirection? }
//                                  → { success, data: { docs, totalDocs, ... }, message }
//
//   POST /admin/delete_user_contact { ids: [id] }
//                                  → { success, message }
//                                  NOTE: takes an ARRAY of ids (legacy quirk).
//                                  We accept a single id and wrap it in [id]
//                                  to match the contract exactly.
//
// NOTE: backend expects 'sortyBy' typo (extra "y"). Preserved.
// ───────────────────────────────────────────────────────────────────────

export type ContactSubmission = {
  _id: string
  name?: string
  email?: string
  subject?: string
  message?: string
  /** "google" | "facebook" | "apple" | "email_password" | other */
  auth_type?: string
  created_at?: string
  updated_at?: string
}

type ContactListParams = {
  page: number
  limit: number
  search?: string
  /** Backend typo preserved: extra "y". */
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type ContactListResponse = {
  docs: ContactSubmission[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

export function useContactList(params: ContactListParams) {
  return useQuery({
    queryKey: ["admin", "contact", "list", params] as const,
    queryFn: async (): Promise<ContactListResponse> => {
      const res = await api.post<{
        success: boolean
        data: ContactListResponse
        message: string
      }>("/admin/list_user_contact", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

export function useDeleteContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      // Legacy sends `{ ids: [id] }` — preserve exactly. Endpoint supports
      // bulk delete in principle but the legacy UI only ever sends one id.
      const res = await api.post("/admin/delete_user_contact", { ids: [id] })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "contact"] })
    },
  })
}
